'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';


interface LoginFormValues {
  email: string;
  password: string;
  remember: boolean;
}

interface DemoCredential {
  role: string;
  email: string;
  password: string;
}

interface LoginFormProps {
  demoCredentials: DemoCredential[];
}

export default function LoginForm({ demoCredentials }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '', remember: false },
  });

  // Backend integration point: POST /api/auth/login with { email, password }
  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    setAuthError('');
    await new Promise((r) => setTimeout(r, 1200));
    const valid = demoCredentials.some((c) => c.email === data.email && c.password === data.password);
    if (!valid) {
      setAuthError('Invalid credentials — use the demo accounts below to sign in');
    } else {
      window.location.href = '/';
    }
    setIsLoading(false);
  };

  const autofill = (cred: DemoCredential) => {
    setValue('email', cred.email);
    setValue('password', cred.password);
    setAuthError('');
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-foreground">Welcome back</h1>
        <p className="text-sm text-muted-foreground mt-1">Sign in to your PrakritiVeer account</p>
      </div>

      {authError && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-[var(--danger-bg)] border border-[var(--danger)]/20 mb-4 animate-slide-up">
          <Icon name="ExclamationCircleIcon" size={16} variant="outline" className="text-[var(--danger)] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[var(--danger)] font-medium">{authError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <div>
          <label className="form-label" htmlFor="login-email">Email Address</label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="your.name@bk.org"
            className={`form-input ${errors.email ? 'form-input-error' : ''}`}
            {...register('email', {
              required: 'Email address is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && <p className="form-error">{errors.email.message}</p>}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="form-label mb-0" htmlFor="login-password">Password</label>
            <button type="button" className="text-xs font-medium text-primary hover:underline">
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              className={`form-input pr-10 ${errors.password ? 'form-input-error' : ''}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} variant="outline" />
            </button>
          </div>
          {errors.password && <p className="form-error">{errors.password.message}</p>}
        </div>

        <div className="flex items-center gap-2">
          <input
            id="login-remember"
            type="checkbox"
            className="rounded border-border accent-primary"
            {...register('remember')}
          />
          <label htmlFor="login-remember" className="text-sm text-muted-foreground cursor-pointer">
            Remember me for 30 days
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3"
          style={{ minWidth: 0 }}
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" />
              Signing in...
            </>
          ) : (
            <>
              <Icon name="ArrowRightOnRectangleIcon" size={16} variant="outline" />
              Sign In
            </>
          )}
        </button>
      </form>

      {/* Demo credentials */}
      <div className="mt-6 p-4 bg-muted/60 rounded-xl border border-border">
        <div className="flex items-center gap-1.5 mb-3">
          <Icon name="InformationCircleIcon" size={14} variant="outline" className="text-muted-foreground" />
          <p className="text-xs font-semibold text-muted-foreground">Demo Accounts — click to autofill</p>
        </div>
        <div className="space-y-2">
          {demoCredentials.map((cred) => (
            <button
              key={`demo-${cred.role}`}
              type="button"
              onClick={() => autofill(cred)}
              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-card border border-border hover:border-primary/40 hover:bg-primary/5 transition-all group text-left"
            >
              <div className="min-w-0">
                <p className="text-xs font-bold text-foreground">{cred.role}</p>
                <p className="text-[11px] text-muted-foreground truncate">{cred.email}</p>
              </div>
              <span className="text-[10px] font-semibold text-primary bg-primary/10 px-2 py-1 rounded-md flex-shrink-0 group-hover:bg-primary group-hover:text-white transition-all">
                Use
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}